import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ApiResponse, PaginatedResponse } from "@/lib/types";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

export interface Coupon {
  _id: string;
  image?: string;
  code: string;
  description?: string;
  type: "fixed" | "percentage" | "free-shipping";
  discountAmount: number;
  minimumPurchaseAmount: number;
  isVerifiedCustomer?: boolean;
  isApproved?: boolean;
  activeDate?: string;
  expireDate: string;
  createdAt: string;
  updatedAt: string;
}

export const couponApi = createApi({
  reducerPath: "couponApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Coupons"],
  endpoints: (builder) => ({
    createCoupon: builder.mutation<ApiResponse<Coupon>, Partial<Coupon>>({
      query: (data) => ({
        url: "/coupon/create-coupon",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Coupons"],
    }),
    getAllCoupons: builder.query<
      PaginatedResponse<Coupon>,
      { limit?: number; searchTerm?: string; page?: number } | void
    >({
      query: (params) => ({
        url: "/coupon",
        params: params || undefined,
      }),
      providesTags: ["Coupons"],
    }),
    getCouponById: builder.query<ApiResponse<Coupon>, string>({
      query: (id) => `/coupon/${id}`,
      providesTags: ["Coupons"],
    }),
    updateCoupon: builder.mutation<
      ApiResponse<Coupon>,
      { id: string; data: Partial<Coupon> }
    >({
      query: ({ id, data }) => ({
        url: `/coupon/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Coupons"],
    }),
    deleteCoupon: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/coupon/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Coupons"],
    }),
  }),
});

export const {
  useCreateCouponMutation,
  useGetAllCouponsQuery,
  useGetCouponByIdQuery,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
} = couponApi;
